import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { authAPI } from '../../utils/api';

const DocumentUpload = () => {
    const navigate = useNavigate();
    const { user, getRedirectPath, loading } = useAuth();
    const { showSuccess, showError } = useNotification();
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Auto-redirect if already uploaded or no user
    React.useEffect(() => {
        if (loading) return;

        if (!user) {
            navigate('/role-selection');
            return;
        }

        const currentPath = getRedirectPath(user);
        if (currentPath !== '/onboarding/documents') {
            navigate(currentPath);
        }
    }, [user, loading, navigate, getRedirectPath]);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);

        // Total limit check
        if (files.length + selectedFiles.length > 10) {
            showError('You can only upload up to 10 documents.');
            return;
        }

        const errors = [];
        const validFiles = selectedFiles.filter(file => {
            const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file.type);
            const isValidSize = file.size <= 5 * 1024 * 1024;

            if (!isValidType) errors.push(`${file.name}: invalid format`);
            if (!isValidSize) errors.push(`${file.name}: too large (max 5MB)`);

            return isValidType && isValidSize;
        });
        if (errors.length > 0) {
            showError(errors.slice(0, 3).join('\n') + (errors.length > 3 ? ` (+${errors.length - 3} more)` : ''));
        }

        const newFiles = validFiles.map(file => ({
            file,
            preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
            id: Math.random().toString(36).substring(7)
        }));

        setFiles(prev => [...prev, ...newFiles]);
    };

    const removeFile = (id) => {
        setFiles(prev => prev.filter(f => f.id !== id));
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            showError('Please select at least one document.');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        const userId = user?._id || user?.id;

        if (!userId) {
            showError('User session error. Please log in again.');
            setUploading(false);
            return;
        }

        formData.append('user_id', userId);
        formData.append('document_type', 'onboarding');

        files.forEach(f => {
            formData.append('documents', f.file);
        });

        try {
            const res = await authAPI.uploadDocumentsInternal(formData);
            if (res.data?.success) {
                showSuccess('Documents uploaded successfully!');
                // Refresh profile to get updated documents_uploaded status
                window.location.reload();
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Failed to upload documents');
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-dark flex items-center justify-center">
                <div className="text-gold">Loading...</div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto py-12 px-4">
            <div className="bg-gray-800 rounded-2xl p-8 shadow-xl">
                <h1 className="text-3xl font-bold text-gold mb-2 text-center">Verify Your Account</h1>
                <p className="text-gray-400 text-center mb-8">
                    Please upload your professional documents (License, ID, etc.) for admin verification.
                    You can upload up to 10 files (JPG, PNG, PDF).
                </p>

                <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-600 rounded-xl p-12 text-center cursor-pointer hover:border-gold transition-colors mb-6 group"
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple
                        accept="image/jpeg,image/png,application/pdf"
                        className="hidden"
                    />
                    <div className="text-gray-400 group-hover:text-gold transition-colors">
                        <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-lg font-medium">Click to select files</p>
                        <p className="text-sm">Max 5MB per file</p>
                    </div>
                </div>

                {files.length > 0 && (
                    <div className="space-y-3 mb-8">
                        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Selected Files ({files.length}/10)</h2>
                        <div className="grid grid-cols-1 gap-3">
                            {files.map((file) => (
                                <div key={file.id} className="flex items-center justify-between bg-gray-700/50 p-3 rounded-lg border border-gray-600">
                                    <div className="flex items-center space-x-3 overflow-hidden">
                                        {file.preview ? (
                                            <img src={file.preview} alt="preview" className="w-10 h-10 object-cover rounded shadow" />
                                        ) : (
                                            <div className="w-10 h-10 flex items-center justify-center bg-gray-600 rounded">
                                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </div>
                                        )}
                                        <span className="truncate text-sm font-medium">{file.file.name}</span>
                                    </div>
                                    <button
                                        onClick={() => removeFile(file.id)}
                                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <button
                    onClick={handleUpload}
                    disabled={uploading || files.length === 0}
                    className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${uploading || files.length === 0
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-gold text-dark shadow-lg shadow-gold/20 hover:scale-[1.02] active:scale-[0.98]'
                        }`}
                >
                    {uploading ? (
                        <div className="flex items-center justify-center space-x-2">
                            <svg className="animate-spin h-5 w-5 text-dark" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Uploading Documents...</span>
                        </div>
                    ) : (
                        'Submit for Verification'
                    )}
                </button>
            </div>
        </div>
    );
};

export default DocumentUpload;
